import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOrder } from '../state/OrderContext';
import { useLocation } from '../state/LocationContext';
import { rankStores } from '../lib/rankStores';
import { recommendationFor } from '../lib/recommendedBrands';
import { formatDistance, measurementSystemFor } from '../lib/geo';
import { listCountries, regionFromLocale } from '../lib/countries';
import { createRequestGuard } from '../lib/requestGuard';
import { searchNearbyStores, isLookupCancellation } from '../services/storeSearch';
import { AlertIcon, ChevronRightIcon, PinIcon, StoreIcon } from './icons';
import './StorePicker.css';

// Where to buy the list.
//
// What this screen can honestly say, and the line it does not cross:
//
//   It shows real grocery branches near the user, from a live places provider,
//   with real addresses, ratings and websites. It does NOT know whether any of
//   them does online pickup. Nothing here is labelled "pickup" — the action is
//   "Visit store website", and the note tells the user to check there. Verified
//   pickup is a later stage and will look different when it arrives.
//
// Fewer than three results is a normal outcome and is shown as-is. Zero is
// also an outcome: the recipe and the shopping list stay exactly where they
// are, and the message says what happened.

// Matches RADIUS_DEFAULT_M in lib/placesRequest.js. Wide enough that a real
// supermarket is in range in most suburbs; the empty-state message below quotes
// this number back, so the user knows how far we actually looked.
const SEARCH_RADIUS_M = 8000;

function ratingLine(store) {
  if (typeof store.rating !== 'number') return null;
  const reviews = store.reviewCount;
  // A rating with no review count behind it is stated bare rather than
  // dressed up with a number we do not have.
  return reviews ? `${store.rating.toFixed(1)} ★ (${reviews.toLocaleString()})` : `${store.rating.toFixed(1)} ★`;
}

function messageForReason(reason) {
  switch (reason) {
    case 'unconfigured':
      return 'Store lookup is not set up on the server yet, so we cannot search for shops.';
    case 'rate-limited':
      return 'Too many lookups just now. Give it a moment.';
    case 'quota':
      return 'The store service has hit its limit for now.';
    case 'timeout':
      return 'The store service took too long to answer.';
    case 'network':
      return 'We could not reach the store service.';
    default:
      return 'The store service could not answer.';
  }
}

function ManualLocationForm({ onDone, busy }) {
  const { setManualLocation } = useLocation();
  const countries = useMemo(() => listCountries(), []);
  const [query, setQuery] = useState('');
  const [countryCode, setCountryCode] = useState(() => regionFromLocale() ?? 'US');
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setSubmitting(true);
    const ok = await setManualLocation({ query: query.trim(), countryCode });
    setSubmitting(false);
    if (ok && onDone) onDone();
  }

  return (
    <form className="location-form" onSubmit={submit}>
      <label className="location-form__label" htmlFor="location-query">
        City or postal code
      </label>
      <div className="location-form__row">
        <input
          id="location-query"
          className="location-form__input"
          value={query}
          placeholder="e.g. Bristol or BS1 4DJ"
          autoComplete="postal-code"
          onChange={(event) => setQuery(event.target.value)}
        />
        {countries.length > 0 ? (
          <select
            className="location-form__country"
            value={countryCode}
            aria-label="Country"
            onChange={(event) => setCountryCode(event.target.value)}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="location-form__country"
            value={countryCode}
            aria-label="Country code"
            maxLength={2}
            placeholder="GB"
            onChange={(event) => setCountryCode(event.target.value.toUpperCase())}
          />
        )}
      </div>
      <button type="submit" className="button button--primary location-form__submit" disabled={submitting || busy}>
        {submitting ? 'Looking that up…' : 'Use this location'}
      </button>
    </form>
  );
}

function StoreRow({ store, system, approximate, variant, onChoose }) {
  const distance = formatDistance(store.distanceMeters, { system, approximate });
  const rating = ratingLine(store);
  const recommendation = recommendationFor(store);

  return (
    <div className={`store-row store-row--${variant}`}>
      <span className="store-row__logo">
        <StoreIcon />
      </span>
      <span className="store-row__body">
        <span className="store-row__name">{store.name}</span>
        <span className="meta-row store-row__meta">
          <PinIcon size={13} /> {distance}
          {store.address ? ` · ${store.address}` : ''}
        </span>
        {rating && <span className="store-row__rating">{rating}</span>}
        {/* Said in our own voice, and attributed. Everything else on this row
            came from the provider; this one line did not, and the user is owed
            the difference. Note what it does not say: nothing about stock,
            nothing about pickup, and no superlative — a recommendation is a
            person's view of a chain, not a check we ran on this branch. */}
        {recommendation && (
          <span className="store-row__pick">
            <span className="store-row__pick-label">Our pick</span> {recommendation.reason}
          </span>
        )}
        {/* The chosen store's own actions live in PickupSection directly
            below, so this row does not repeat them — two identical "visit"
            buttons stacked is a worse screen than one. Alternatives keep the
            link, because checking a shop is how you decide to switch to it. */}
        {variant === 'alternative' && (
          <span className="store-row__actions">
            {/* Never "Order pickup", never "Pickup available" — we have not
                checked, and the label must not imply that we have. */}
            <a className="store-row__link" href={store.website} target="_blank" rel="noreferrer">
              Visit store website <ChevronRightIcon size={14} />
            </a>
            {onChoose && (
              <button type="button" className="store-row__choose" onClick={() => onChoose(store)}>
                Use this store
              </button>
            )}
          </span>
        )}
      </span>
    </div>
  );
}

export default function StorePicker() {
  const { store, storeOptions, update } = useOrder();
  const location = useLocation();

  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [showManual, setShowManual] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);

  const guardRef = useRef(createRequestGuard());
  const abortRef = useRef(null);

  const coords = location.coords;
  const approximate = location.isApproximate;
  const system = measurementSystemFor(coords?.countryCode ?? regionFromLocale());

  // The lookup runs when — and only when — there is a location to run it
  // against. No location, no request, and no provider call the user did not
  // ask for.
  useEffect(() => {
    if (!coords) return undefined;

    const token = guardRef.current.begin();
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    setStatus('loading');
    setError(null);

    (async () => {
      try {
        const found = await searchNearbyStores({
          lat: coords.lat,
          lon: coords.lon,
          radiusMeters: SEARCH_RADIUS_M,
          signal: controller.signal,
        });
        if (!guardRef.current.isCurrent(token)) return;

        const ranked = rankStores(found, { lat: coords.lat, lon: coords.lon });
        // The chosen store survives a re-search only if it is still one of the
        // results; otherwise the top result takes over rather than leaving a
        // selection pointing at a shop we no longer list.
        const keep = ranked.find((candidate) => candidate.id && candidate.id === store?.id);
        update({ storeOptions: ranked, store: keep ?? ranked[0] ?? null });
        setShowAlternatives(false);
        setStatus('ready');
      } catch (err) {
        if (!guardRef.current.isCurrent(token) || isLookupCancellation(err)) return;
        setError({ reason: err?.reason ?? 'provider', retryable: Boolean(err?.retryable) });
        setStatus('error');
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords, attempt]);

  const alternatives = (storeOptions ?? []).filter((candidate) => candidate !== store);

  return (
    <section className="card">
      <p className="card__label">Where to get them</p>

      {!coords && location.status === 'idle' && (
        <div className="location-ask">
          <p className="location-ask__text">
            We&rsquo;ll look for grocery shops near you. Your location is used for this search and
            nothing else — we don&rsquo;t track it.
          </p>
          <button type="button" className="button button--primary" onClick={location.requestDeviceLocation}>
            Find shops near me
          </button>
          <button type="button" className="link-button" onClick={() => setShowManual(true)}>
            Enter a city or postal code instead
          </button>
          {showManual && <ManualLocationForm onDone={() => setShowManual(false)} />}
        </div>
      )}

      {location.status === 'prompting' && (
        <p className="status">
          <span className="spinner" /> Getting your location…
        </p>
      )}

      {/* A refusal is not an error. It goes straight to the one manual
          fallback, with no second prompt and no nagging. */}
      {(location.status === 'denied' || location.status === 'unavailable') && (
        <div className="location-ask">
          <p className="location-ask__text">
            {location.status === 'denied'
              ? 'No problem — tell us roughly where you are instead.'
              : location.error ?? 'Your device could not provide a location.'}
          </p>
          {location.error && location.status === 'denied' && (
            <p className="status status--error">
              <AlertIcon size={16} /> {location.error}
            </p>
          )}
          <ManualLocationForm />
        </div>
      )}

      {coords && status === 'loading' && (
        <p className="status">
          <span className="spinner" /> Looking for shops near you…
        </p>
      )}

      {coords && status === 'error' && (
        <p className="status status--error">
          <AlertIcon size={16} /> {messageForReason(error?.reason)}
          {error?.retryable && (
            <button type="button" className="retry-button" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </button>
          )}
        </p>
      )}

      {coords && status === 'ready' && !store && (
        <p className="store-empty">
          No grocery shops with a website came back within{' '}
          {formatDistance(SEARCH_RADIUS_M, { system })} of here. Your recipe and shopping list are
          unchanged — you can set a different location and try again.
        </p>
      )}

      {coords && status === 'ready' && store && (
        <>
          <StoreRow store={store} system={system} approximate={approximate} variant="primary" />

          {/* The claim about pickup used to be made here. It now sits in
              PickupSection, beside the button it qualifies — a disclaimer is
              only doing its job next to the action it is about. */}

          {alternatives.length > 0 && (
            <>
              <button
                type="button"
                className="link-button store-change"
                aria-expanded={showAlternatives}
                onClick={() => setShowAlternatives((open) => !open)}
              >
                {showAlternatives ? 'Hide other stores' : `Change store (${alternatives.length})`}
              </button>
              {showAlternatives && (
                <div className="store-alternatives">
                  {alternatives.map((candidate) => (
                    <StoreRow
                      key={candidate.id ?? candidate.name}
                      store={candidate}
                      system={system}
                      approximate={approximate}
                      variant="alternative"
                      onChoose={(chosen) => {
                        update({ store: chosen });
                        setShowAlternatives(false);
                      }}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Available whenever we have a location, not just when it is rough:
          the user may simply be shopping somewhere other than where they
          stand. When the fix IS rough, we say so before they wonder. */}
      {coords && (
        <div className="location-footer">
          <p className="location-footer__text">
            {approximate
              ? `Distances are approximate — ${
                  coords.source === 'manual'
                    ? 'they are measured from the place you entered'
                    : 'your device gave us a rough position'
                }.`
              : 'Distances are straight-line, not driving distance.'}
            {coords.label ? ` Searching near ${coords.label}.` : ''}
          </p>
          <button type="button" className="link-button" onClick={() => setShowManual((open) => !open)}>
            {showManual ? 'Cancel' : 'Set my location'}
          </button>
          {showManual && <ManualLocationForm onDone={() => setShowManual(false)} busy={status === 'loading'} />}
        </div>
      )}
    </section>
  );
}
